/**
 * UGC showcase, ported from the Roots (Horizon) theme.
 *
 * Prestige has no Horizon slideshow, so this element owns the carousel: the row is a native
 * scroll-snap scroller, the "active" slide is the one nearest the middle (or start edge when
 * centering is off), and only that slide plays its video and shows its product card.
 *
 * Looping copies the whole set of slides onto each end of the row. When scrolling comes to rest
 * on a copy, the row jumps instantly to the matching real slide, which looks identical.
 */
class UgcShowcase extends HTMLElement {
  connectedCallback() {
    this.isVisible = false;
    this.hasBeenSeen = false;
    this.scroller = this.querySelector('[data-ugc-showcase-scroller]');
    this.querySelectorAll('[data-ugc-clone]').forEach((clone) => clone.remove());
    this.realSlides = Array.from(this.querySelectorAll('.ugc-showcase__slide'));
    if (!this.scroller || this.realSlides.length === 0) return;

    this.loops = this.dataset.loop === 'true' && this.realSlides.length > 1;
    if (this.loops) this.addLoopClones();
    this.slides = Array.from(this.querySelectorAll('.ugc-showcase__slide'));

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.scroller.addEventListener('scroll', this.handleScroll.bind(this), { passive: true, signal });
    if (this.loops && 'onscrollend' in window) {
      this.scroller.addEventListener('scrollend', () => this.wrapToRealSlide(), { signal });
    }
    this.addEventListener('click', this.handleClick.bind(this), { signal });
    document.addEventListener('visibilitychange', () => this.updateVideos(), { signal });

    // Off-screen carousels have no layout to measure, so the opening jump waits for first view.
    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.isVisible = Boolean(entry?.isIntersecting);
        if (this.isVisible && !this.hasBeenSeen) {
          this.hasBeenSeen = true;
          requestAnimationFrame(() => this.jumpToInitialSlide());
        }
        this.updateVideos();
      },
      { threshold: 0.1 }
    );
    this.observer.observe(this);

    this.setActiveSlide(this.realSlides[0]);
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.observer?.disconnect();
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    clearTimeout(this.settleTimer);
  }

  /** One full copy of the slides before the first and after the last, hidden from assistive tech. */
  addLoopClones() {
    const makeClone = (slide) => {
      const clone = slide.cloneNode(true);
      clone.realSlide = slide;
      clone.dataset.ugcClone = '';
      clone.classList.remove('is-active');
      clone.setAttribute('aria-hidden', 'true');

      // Drop theme editor hooks, the quick-buy modal (the real slide owns that id) and any other ids.
      clone.removeAttribute('data-shopify-editor-block');
      clone.querySelectorAll('quick-buy-modal').forEach((modal) => modal.remove());
      clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      clone.querySelectorAll('a, button, input').forEach((control) => control.setAttribute('tabindex', '-1'));
      clone.querySelectorAll('video').forEach((video) => (video.preload = 'none'));
      return clone;
    };

    this.scroller.prepend(...this.realSlides.map(makeClone));
    this.scroller.append(...this.realSlides.map(makeClone));
  }

  /** After scrolling stops on a copy, swap to the real slide without any visible movement. */
  wrapToRealSlide() {
    const clone = this.activeSlide;
    if (!this.loops || !clone?.realSlide) return;

    const cloneVideo = clone.querySelector('video');
    const realVideo = clone.realSlide.querySelector('video');
    if (cloneVideo && realVideo && cloneVideo.readyState > 0) realVideo.currentTime = cloneVideo.currentTime;

    this.classList.add('is-looping');
    this.scrollToSlide(clone.realSlide, 'instant');
    requestAnimationFrame(() => requestAnimationFrame(() => this.classList.remove('is-looping')));
  }

  get centersActiveSlide() {
    return this.dataset.centerActiveSlide !== 'false';
  }

  /**
   * Opens on the first slide with enough neighbours before it to fill the centering gutter,
   * so no blank space shows on the left. `data-initial-slide` is the minimum.
   */
  jumpToInitialSlide() {
    // A looping row always opens on the first real slide; the copies before it fill the gutter.
    if (this.loops) {
      this.scrollToSlide(this.realSlides[0], 'instant');
      return;
    }

    // Leave the row alone once a visitor has moved it themselves.
    if (this.scroller.scrollLeft !== 0) return;

    const minimum = Number(this.dataset.initialSlide) || 0;
    if (minimum === 0) return;

    const slideWidth = this.slides[0].getBoundingClientRect().width;
    if (!slideWidth) return;

    const styles = getComputedStyle(this.scroller);
    const gutter = parseFloat(styles.paddingInlineStart) || 0;
    const gap = parseFloat(styles.columnGap) || 0;
    const needed = Math.ceil(gutter / (slideWidth + gap));
    const index = Math.min(Math.max(minimum, needed), this.slides.length - 1);

    this.scrollToSlide(this.slides[index], 'instant');
  }

  scrollToSlide(slide, behavior = 'smooth') {
    const scrollerRect = this.scroller.getBoundingClientRect();
    const slideRect = slide.getBoundingClientRect();
    const isRtl = getComputedStyle(this).direction === 'rtl';

    let offset;
    if (this.centersActiveSlide) {
      offset = slideRect.left + slideRect.width / 2 - (scrollerRect.left + scrollerRect.width / 2);
    } else {
      const padding = parseFloat(getComputedStyle(this.scroller).scrollPaddingInlineStart) || 0;
      offset = isRtl
        ? slideRect.right - (scrollerRect.right - padding)
        : slideRect.left - (scrollerRect.left + padding);
    }

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.scroller.scrollBy({ left: offset, behavior: reduceMotion ? 'instant' : behavior });
    this.setActiveSlide(slide);
  }

  /** The slide nearest the middle of the row, or nearest the start edge when not centering. */
  findActiveSlide() {
    const bounds = this.scroller.getBoundingClientRect();
    const padding = parseFloat(getComputedStyle(this.scroller).scrollPaddingInlineStart) || 0;
    const reference = this.centersActiveSlide ? bounds.left + bounds.width / 2 : bounds.left + padding;

    let closest = null;
    let closestDistance = Infinity;

    for (const slide of this.slides) {
      const rect = slide.getBoundingClientRect();
      const point = this.centersActiveSlide ? rect.left + rect.width / 2 : rect.left;
      const distance = Math.abs(point - reference);
      if (distance < closestDistance) {
        closestDistance = distance;
        closest = slide;
      }
    }

    return closest;
  }

  handleScroll() {
    if (this.scrollFrame) return;

    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;
      this.setActiveSlide(this.findActiveSlide());
    });

    // Browsers without a scrollend event: treat a short pause in scrolling as the end.
    if (this.loops && !('onscrollend' in window)) {
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => this.wrapToRealSlide(), 150);
    }
  }

  handleClick(event) {
    if (!(event.target instanceof Element)) return;

    const navButton = event.target.closest('[data-ugc-showcase-nav]');
    if (navButton) {
      event.preventDefault();
      this.navigate(navButton.dataset.ugcShowcaseNav === 'previous' ? -1 : 1);
      return;
    }

    if (event.target.closest('.ugc-showcase__sound-toggle')) {
      this.dataset.sound = this.dataset.sound === 'on' ? 'off' : 'on';
      this.updateVideos();
      return;
    }

    // Tapping a peeking slide's media brings it to the middle instead of doing nothing.
    const slide = event.target.closest('.ugc-showcase__slide');
    if (slide && slide !== this.activeSlide && event.target.closest('.ugc-showcase__media')) {
      this.scrollToSlide(slide);
    }
  }

  navigate(step) {
    const current = Math.max(this.slides.indexOf(this.activeSlide), 0);

    // Looping steps onto the neighbouring copy so the row keeps moving the same way.
    const next = this.loops
      ? Math.min(Math.max(current + step, 0), this.slides.length - 1)
      : (current + step + this.slides.length) % this.slides.length;
    this.scrollToSlide(this.slides[next]);
  }

  setActiveSlide(slide) {
    if (!(slide instanceof HTMLElement) || slide === this.activeSlide) return;

    this.activeSlide = slide;

    for (const item of this.slides) {
      const isActive = item === slide;
      item.classList.toggle('is-active', isActive);

      // Copies stay out of the tab order entirely.
      if (item.realSlide) continue;

      // Hidden product cards and sound buttons on other slides stay out of the tab order.
      item
        .querySelectorAll('.ugc-showcase__product-card a, .ugc-showcase__product-card button, .ugc-showcase__sound-toggle')
        .forEach((control) => (isActive ? control.removeAttribute('tabindex') : control.setAttribute('tabindex', '-1')));
    }

    this.updateVideos();
  }

  updateVideos() {
    const soundOn = this.dataset.sound === 'on';
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.querySelectorAll('.ugc-showcase__sound-toggle').forEach((button) => {
      button.setAttribute('aria-pressed', String(soundOn));
    });

    this.querySelectorAll('video').forEach((video) => {
      const isActive = video.closest('.ugc-showcase__slide') === this.activeSlide;

      video.muted = !isActive || !soundOn;
      video.playsInline = true;
      video.autoplay = false;

      if (isActive && this.isVisible && !document.hidden && !reduceMotion) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  }
}

if (!customElements.get('ugc-showcase')) {
  customElements.define('ugc-showcase', UgcShowcase);
}
